/**
 * JIS配列キーボード + 指ガイドのデータ(docs/02_game_design.md §3)。
 *
 * - キーは指ごとに色分けする(色はユーザーカスタムできる余地を残すため定数を1か所に集約)
 * - engine の `getState().nextKeys` の文字から光らせるキーを引く
 * - スペースは「␣」表示(Unity版準拠)
 */

/** 指の識別子(左右5本ずつ。親指は共有)。 */
export type FingerId =
  | 'l-pinky'
  | 'l-ring'
  | 'l-middle'
  | 'l-index'
  | 'thumb'
  | 'r-index'
  | 'r-middle'
  | 'r-ring'
  | 'r-pinky';

export type Hand = 'left' | 'right';

/** 指ごとの色(キー配色のカスタム口)。 */
export const FINGER_COLORS: Readonly<Record<FingerId, string>> = {
  'l-pinky': '#ff6b6b',
  'l-ring': '#ff922b',
  'l-middle': '#fcc419',
  'l-index': '#51cf66',
  thumb: '#ced4da',
  'r-index': '#22b8cf',
  'r-middle': '#4dabf7',
  'r-ring': '#845ef7',
  'r-pinky': '#f06595',
};

export const FINGER_LABELS: Readonly<Record<FingerId, string>> = {
  'l-pinky': 'ひだり こゆび',
  'l-ring': 'ひだり くすりゆび',
  'l-middle': 'ひだり なかゆび',
  'l-index': 'ひだり ひとさしゆび',
  thumb: 'おやゆび',
  'r-index': 'みぎ ひとさしゆび',
  'r-middle': 'みぎ なかゆび',
  'r-ring': 'みぎ くすりゆび',
  'r-pinky': 'みぎ こゆび',
};

/** 指が属する手。 */
export function handOf(finger: FingerId): Hand | 'both' {
  if (finger === 'thumb') return 'both';
  return finger.startsWith('l-') ? 'left' : 'right';
}

/** キーボードの1キー。 */
export interface KeyDef {
  /** 一意なキーID(= KeyboardEvent.code 相当)。 */
  readonly id: string;
  /** 通常時に打てる文字(なければ機能キー)。 */
  readonly base?: string;
  /** Shift併用で打てる文字。 */
  readonly shifted?: string;
  /** キートップ表示(省略時は base/shifted から作る)。 */
  readonly label?: string;
  readonly finger: FingerId;
  /** 幅(1 = 標準キー)。 */
  readonly width?: number;
  /** ホームポジションの突起があるキー(F / J)。 */
  readonly home?: boolean;
}

const K = (
  id: string,
  base: string,
  shifted: string | undefined,
  finger: FingerId,
  extra: Partial<KeyDef> = {},
): KeyDef => ({
  id,
  base,
  ...(shifted === undefined ? {} : { shifted }),
  finger,
  ...extra,
});

const F = (id: string, label: string, finger: FingerId, width: number): KeyDef => ({
  id,
  label,
  finger,
  width,
});

/** JIS配列(主要4段+スペース段)。 */
export const KEYBOARD_ROWS: readonly (readonly KeyDef[])[] = [
  [
    K('Digit1', '1', '!', 'l-pinky'),
    K('Digit2', '2', '"', 'l-ring'),
    K('Digit3', '3', '#', 'l-middle'),
    K('Digit4', '4', '$', 'l-index'),
    K('Digit5', '5', '%', 'l-index'),
    K('Digit6', '6', '&', 'r-index'),
    K('Digit7', '7', "'", 'r-index'),
    K('Digit8', '8', '(', 'r-middle'),
    K('Digit9', '9', ')', 'r-ring'),
    K('Digit0', '0', undefined, 'r-pinky'),
    K('Minus', '-', '=', 'r-pinky'),
    K('Equal', '^', '~', 'r-pinky'),
    K('IntlYen', '\\', '|', 'r-pinky'),
  ],
  [
    F('Tab', 'Tab', 'l-pinky', 1.5),
    K('KeyQ', 'q', undefined, 'l-pinky'),
    K('KeyW', 'w', undefined, 'l-ring'),
    K('KeyE', 'e', undefined, 'l-middle'),
    K('KeyR', 'r', undefined, 'l-index'),
    K('KeyT', 't', undefined, 'l-index'),
    K('KeyY', 'y', undefined, 'r-index'),
    K('KeyU', 'u', undefined, 'r-index'),
    K('KeyI', 'i', undefined, 'r-middle'),
    K('KeyO', 'o', undefined, 'r-ring'),
    K('KeyP', 'p', undefined, 'r-pinky'),
    K('BracketLeft', '@', '`', 'r-pinky'),
    K('BracketRight', '[', '{', 'r-pinky'),
  ],
  [
    F('CapsLock', 'Caps', 'l-pinky', 1.8),
    K('KeyA', 'a', undefined, 'l-pinky'),
    K('KeyS', 's', undefined, 'l-ring'),
    K('KeyD', 'd', undefined, 'l-middle'),
    K('KeyF', 'f', undefined, 'l-index', { home: true }),
    K('KeyG', 'g', undefined, 'l-index'),
    K('KeyH', 'h', undefined, 'r-index'),
    K('KeyJ', 'j', undefined, 'r-index', { home: true }),
    K('KeyK', 'k', undefined, 'r-middle'),
    K('KeyL', 'l', undefined, 'r-ring'),
    K('Semicolon', ';', '+', 'r-pinky'),
    K('Quote', ':', '*', 'r-pinky'),
    K('Backslash', ']', '}', 'r-pinky'),
  ],
  [
    F('ShiftLeft', 'Shift', 'l-pinky', 2.3),
    K('KeyZ', 'z', undefined, 'l-pinky'),
    K('KeyX', 'x', undefined, 'l-ring'),
    K('KeyC', 'c', undefined, 'l-middle'),
    K('KeyV', 'v', undefined, 'l-index'),
    K('KeyB', 'b', undefined, 'l-index'),
    K('KeyN', 'n', undefined, 'r-index'),
    K('KeyM', 'm', undefined, 'r-index'),
    K('Comma', ',', '<', 'r-middle'),
    K('Period', '.', '>', 'r-ring'),
    K('Slash', '/', '?', 'r-pinky'),
    K('IntlRo', '\\', '_', 'r-pinky'),
  ],
  [K('Space', ' ', undefined, 'thumb', { label: '␣', width: 7 })],
];

/** 全キーの平坦なリスト。 */
export const ALL_KEYS: readonly KeyDef[] = KEYBOARD_ROWS.flat();

/** キートップの表示文字列。 */
export function keyLabel(key: KeyDef): string {
  if (key.label !== undefined) return key.label;
  const base = key.base ?? '';
  if (key.shifted !== undefined && key.shifted !== base) return `${key.shifted}\n${base}`;
  return base.toUpperCase();
}

interface CharBinding {
  readonly keyId: string;
  readonly finger: FingerId;
  readonly shift: boolean;
}

function buildCharMap(): ReadonlyMap<string, CharBinding> {
  const map = new Map<string, CharBinding>();
  for (const key of ALL_KEYS) {
    if (key.base !== undefined && !map.has(key.base)) {
      map.set(key.base, { keyId: key.id, finger: key.finger, shift: false });
      if (/^[a-z]$/.test(key.base)) {
        map.set(key.base.toUpperCase(), { keyId: key.id, finger: key.finger, shift: true });
      }
    }
    if (key.shifted !== undefined && !map.has(key.shifted)) {
      map.set(key.shifted, { keyId: key.id, finger: key.finger, shift: true });
    }
  }
  return map;
}

const CHAR_MAP = buildCharMap();

/** 打鍵文字 → キー割り当て(未対応文字は null)。 */
export function bindingForChar(char: string): CharBinding | null {
  return CHAR_MAP.get(char) ?? CHAR_MAP.get(char.toLowerCase()) ?? null;
}

/** `nextKeys` から光らせるキーIDの集合を作る。 */
export function highlightedKeyIds(nextKeys: readonly string[]): ReadonlySet<string> {
  const ids = new Set<string>();
  for (const char of nextKeys) {
    const binding = bindingForChar(char);
    if (binding) ids.add(binding.keyId);
  }
  return ids;
}

/** `nextKeys` から使う指の集合を作る(手のシルエット強調用)。 */
export function highlightedFingers(nextKeys: readonly string[]): ReadonlySet<FingerId> {
  const fingers = new Set<FingerId>();
  for (const char of nextKeys) {
    const binding = bindingForChar(char);
    if (binding) fingers.add(binding.finger);
  }
  return fingers;
}
